import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@lib/api";
import { API_ROUTES } from "../constants";
import { QUERY_KEY, UserData } from "./useGetUser";

interface WalkthroughsFinishedPayload {
  walkthroughId: number;
  isOnboarding: boolean;
}

export const useUpdateWalkthroughsFinished = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: WalkthroughsFinishedPayload) => {
      // fire and forget
      await apiClient.post(API_ROUTES.walkthroughsFinished, payload);
    },
    onMutate: async (payload: WalkthroughsFinishedPayload) => {
      // Cancel any outgoing fetches for get user
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      // Snapshot the previous value
      const previousUserData = queryClient.getQueryData<UserData>([QUERY_KEY]);

      // Optimistically update state
      queryClient.setQueryData([QUERY_KEY], (prevState: UserData) => {
        if (!prevState) {
          return payload;
        }

        const newFinishedWalkthrough = {
          walkthroughId: String(payload.walkthroughId),
          createdAt: new Date().toISOString(),
          isOnboarding: payload.isOnboarding,
        };

        return {
          user: prevState.user,
          finishedWalkthroughs: [
            ...(prevState.finishedWalkthroughs ?? []),
            newFinishedWalkthrough,
          ],
        };
      });

      return { previousUserData };
    },
    onError: (
      error: unknown,
      _,
      context: { previousUserData?: UserData } | undefined
    ) => {
      console.error("Failed to update walkthroughs finished:", error);
      // rollback to previous state
      if (context?.previousUserData) {
        queryClient.setQueryData([QUERY_KEY], context.previousUserData);
      }
    },
    // refetch after mutation
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
